// Ảnh đại diện của gói khách đặt, hiện bên trái tên gói ở thẻ Booking online.
//
// Mã gói của booking (`serviceCode`) chính là `products.code`, nên ảnh lấy
// ĐÚNG luật của form đơn (packageImageOf): ảnh bìa concept của gói → ảnh
// concept trùng mã gói → ảnh riêng của gói. Cùng một gói phải hiện cùng một
// ảnh ở form đơn và ở đây. KHÔNG mượn ảnh gói kề (trang khách cũng đã bỏ, 03/10/2026): người xem là
// CSKH đang gọi chốt với khách, ảnh của gói khác dễ làm tư vấn nhầm gói.
//
// Kho ảnh hay danh mục lỗi thì chỉ mất ảnh (rơi về ô máy ảnh) — bảng booking
// không được chết theo.

import { useMemo, useState } from "react";
import { Camera } from "lucide-react";

import { toPickerProduct, useProductCatalog } from "@/features/products/catalogApi";
import { packageImageOf, type ConceptCoverMap } from "@/features/products/packageImage";
import { useLookbookConcepts } from "@/features/lookbook/api";
import { coverImageOf } from "@/features/lookbook/helpers";
import { imageUrl } from "@/lib/upload";
import { cn } from "@/lib/utils";

/** Mã gói → đường dẫn ảnh cỡ nhỏ; `undefined` = gói chưa có ảnh. */
export function useServiceImageOf(): (
  serviceCode: string | null | undefined,
) => string | undefined {
  const { products } = useProductCatalog();
  // Đọc cả concept đã ẩn: booking cũ có thể còn gắn gói dùng concept cũ.
  const { data: concepts } = useLookbookConcepts(undefined, true);
  return useMemo(() => {
    const covers: ConceptCoverMap = Object.fromEntries(
      (concepts ?? []).map((c) => [c.id, coverImageOf(c)]),
    );
    const byCode = new Map(products.map((p) => [p.code, toPickerProduct(p)]));
    return (serviceCode) => {
      if (!serviceCode) return undefined;
      const raw = packageImageOf(byCode.get(serviceCode), covers);
      return raw ? imageUrl(raw, "thumb") : undefined;
    };
  }, [products, concepts]);
}

export function ServiceThumb({ src, className }: { src: string | undefined; className?: string }) {
  // Ảnh hỏng thì về ô máy ảnh thay vì khung ảnh vỡ. Đổi `src` là thử lại từ đầu.
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const box = cn("size-10 shrink-0 rounded-md", className);
  if (!src || brokenSrc === src) {
    return (
      <div
        className={cn(box, "flex items-center justify-center bg-muted text-muted-foreground")}
        aria-hidden
      >
        <Camera className="size-4" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      loading="lazy"
      className={cn(box, "border object-cover")}
      onError={() => setBrokenSrc(src)}
    />
  );
}
